/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import { type useCurrentTheme as useCurrentThemeOfSDK } from '@trebox-arch/foundation-sdk';
import { useTheme } from '@coze-arch/coze-design';

export const useCurrentTheme: typeof useCurrentThemeOfSDK = () =>
  useTheme().theme;

// Theme controls — surface for app-level UI (e.g. account menu's Theme
// switcher). Wraps coze-design's useTheme so app code stays free of direct
// @coze-arch/* imports per docs/frontend/stack.md §3.2.
export type TreboxTheme = 'light' | 'dark' | 'system';

export const useThemeControls = (): {
  theme: TreboxTheme;
  setTheme: (t: TreboxTheme) => void;
} => {
  const { theme, setTheme } = useTheme();
  return { theme: theme as TreboxTheme, setTheme };
};

// Resolved theme: 'system' is translated to the concrete OS preference
// ('light' | 'dark') so consumers can apply `data-theme` deterministically.
// Re-renders when the OS preference changes while the user is on 'system'.
export const useResolvedTheme = (): 'light' | 'dark' => {
  const { theme } = useTheme();
  const [osDark, setOsDark] = useState(() => readPrefersDark());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setOsDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  if (theme === 'dark') {
    return 'dark';
  }
  if (theme === 'light') {
    return 'light';
  }
  return osDark ? 'dark' : 'light';
};

const readPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export { logoutOnly, uploadAvatar } from './passport';

export {
  getIsSettled,
  getIsLogined,
  getUserInfo,
  getUserAuthInfos,
  useIsSettled,
  useIsLogined,
  useUserInfo,
  useUserAuthInfo,
  useUserLabel,
  subscribeUserAuthInfos,
  refreshUserInfo,
  useLoginStatus,
  getLoginStatus,
} from './user';

export { BackButton, SideSheetMenu } from '@trebox-foundation/layout';

export { useSpace } from './space';
